import React, { useState } from "react";
import { useVariableValue } from "@devcycle/react-client-sdk";
import styled from "@emotion/styled";

import { generateNoticeSearchUrl } from "components/notices/utils";
import { CreateOpportunityDrawer } from "components/opportunities/create_opportunity/CreateOpportunityDrawer";
import { CreditAccessWrapper } from "components/paywall/CreditAccessWrapper";
import { MAY_4TH_ACTIVATION } from "lib/featureFlags";
import { ViewConfigurationFilterSettingsRelevanceScoreEnum as RelevanceScoreEnum } from "lib/generated/app-api";
import { useRemainingCredits } from "lib/hooks/api/admin/credits/useRemainingCredits";
import { useSubscription } from "lib/hooks/subscription";
import { usePaywall } from "lib/providers/Paywall";
import { routes } from "lib/routes";
import { EventNames, useTracking } from "lib/tracking";
import { NoticesStagesv2 } from "lib/types/graphQLEnums";
import { Flex, Text } from "../../styles/utility-components";
import CreateBidModal from "./CreateBidModal";
import { DEFAULT_VIEW_IDS } from "./Opportunities";
import PartCard from "./PartCard";

function getNoticeUrlWithView(viewId: string) {
  const url = new URL(routes.notices, window.location.origin);
  url.searchParams.set("current_view_id", viewId);
  return url.toString();
}

function Welcome() {
  const subscription = useSubscription();
  const { logEvent } = useTracking();

  const hasBidsAccess = subscription.hasDataTypes("BIDS");

  const [bidModalOpen, setBidModalOpen] = useState(false);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDrawerClosing, setIsDrawerClosing] = useState(false);

  const handleDrawerClose = () => {
    setIsDrawerClosing(true);
    setTimeout(() => {
      setIsDrawerOpen(false);
      setIsDrawerClosing(false);
    }, 300); // timeout duration to match the drawer closing animation duration
  };

  const { openPaywallModal } = usePaywall();

  const { data: credits } = useRemainingCredits(
    { creditType: "BID" },
    {
      enabled: !hasBidsAccess,
    },
  );

  // TODO: hook up to consumption model using useCheckCreditsAccess - like bids above
  const hasMarketAnalysisAccess = subscription.hasDataTypes("MARKET_ANALYSIS");
  const mayActivationEnabled = useVariableValue(MAY_4TH_ACTIVATION, false);
  if (window.currentUser === null) {
    return null;
  }
  const firstName = window.currentUser.first_name;

  return (
    <Flex flexDirection="column" gap="24px">
      <TitleText>
        Welcome to Stotles, <TitleTextName as="span">{firstName}</TitleTextName>
      </TitleText>
      {mayActivationEnabled && (
        <WelcomeContent flexDirection="column" gap={16}>
          <Text bold>Where would you like to get started?</Text>
          <CardsContainer>
            <PartCard
              partType="Strategy"
              hasPaywall={!hasMarketAnalysisAccess}
              href={generateNoticeSearchUrl({
                publishDate: {
                  relativeFrom: "P-3Y",
                },
                stage: [NoticesStagesv2.AwardedContract, NoticesStagesv2.ExpiredContract],
                relevanceScore: [RelevanceScoreEnum.Low],
              })}
              onClick={(e) => {
                if (!hasMarketAnalysisAccess) {
                  e.preventDefault();
                  openPaywallModal("STRATEGY", {
                    requiredDataSubscription: "MARKET_ANALYSIS",
                    strategyPaywall: true,
                  });
                  return;
                }
                logEvent(EventNames.createStrategyStarted);
              }}
            />
            <PartCard
              partType="Pipeline"
              href={getNoticeUrlWithView(DEFAULT_VIEW_IDS.upcomingContractExpiries)}
              onClick={() => logEvent(EventNames.buildPipelineStarted)}
            />
            <PartCard
              partType="Tenders"
              // Navigate to notice search with open tenders view selected
              href={getNoticeUrlWithView(
                window.settings?.openOppsViewId ?? DEFAULT_VIEW_IDS.openOpportunities,
              )}
              onClick={() => logEvent(EventNames.trackTendersStarted)}
            />
            <CreditAccessWrapper requiredDataType="BIDS" creditType="BID">
              <PartCard
                partType="Bids"
                hasPaywall={credits && credits.remainingCredits <= 0}
                onClick={() => {
                  // Have to include this otherwise a quick click will get them through paywall
                  if (hasBidsAccess || (credits && credits?.remainingCredits > 0)) {
                    logEvent(EventNames.winBidsStarted);
                    setBidModalOpen(true);
                  }
                }}
              />
            </CreditAccessWrapper>
          </CardsContainer>
        </WelcomeContent>
      )}
      <CreateBidModal
        isOpen={bidModalOpen}
        setOpen={setBidModalOpen}
        openOpportunityDrawer={() => {
          setIsDrawerOpen(true);
          setBidModalOpen(false);
        }}
      />
      {(isDrawerOpen || isDrawerClosing) && (
        <CreateOpportunityDrawer
          isOpen={isDrawerOpen && !isDrawerClosing}
          onClose={handleDrawerClose}
          hiddenFields={["value", "closeDate"]}
          defaultValues={{
            stage: "OPEN",
            noticeStage: "Tender",
          }}
          navigateToNewOpportunity
        />
      )}
    </Flex>
  );
}

const WelcomeContent = styled(Flex)({
  paddingTop: "24px",
});

const TitleText = styled.h1(({ theme }) => ({
  color: theme.colors.sysTextDefault,
  fontSize: "29px",
  fontWeight: "500",
  lineHeight: "125%",
  margin: 0,
}));

const TitleTextName = styled(TitleText)({
  fontWeight: "700",
});

const CardsContainer = styled.div({
  display: "grid",
  gap: "16px",
  gridTemplateColumns: "repeat(4, 1fr)",
  "@media (max-width: 960px)": {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
});

export default Welcome;
